<script setup>
import {computed, onBeforeMount, ref} from "vue";
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useToast} from "vue-toast-notification";
import {useRouter} from "vue-router";
import Loader from "../../Components/Loader.vue";
import AttachmentFiles from "../../Components/AttachmentFiles.vue";
import Comments from "../../Components/Comments.vue";
import {useAuthUserStore} from "../../Store/AuthUserStore.js";
import FormSelectSearchUser from "../../Components/FormSelectSearchUser.vue";

const { httpErrorsHandler } = useHttpErrorsHandler();
const $toast = useToast();
const router = useRouter();
const authUserStore = useAuthUserStore();
const authUser = computed(() => authUserStore.authUser);

const loading = ref(false)
const ticket = ref(null)
const errors = ref(null)
function getTicket() {
    loading.value = true;
    axios.get("/api/tickets/" + router.currentRoute.value.params.id)
        .then(response => {
            ticket.value = response.data;
            loading.value = false;
            if (ticket.value.data.assigned_to_id === null && ticket.value.data.user_id !== authUser.value.id && (authUser.value.role === 'back_office' || authUser.value.role === 'admin')) {
                assignTicketToUser(authUser.value.id)
            }
            resetNotificationNumber()
        })
        .catch(error => {
            httpErrorsHandler(error);
            loading.value = false;
        });
}
onBeforeMount(() => {
    getTicket()
})

function updateStatus(status) {
    axios.patch("/api/tickets/" + ticket.value.data.id + '/status', { status: status })
        .then(response => {
            ticket.value.data.status = response.data.data.status;
            ticket.value.data.status_label_fr = response.data.data.status_label_fr;
            ticket.value.data.status_badge_color = response.data.data.status_badge_color;
            $toast.success('Le status du ticket a été mis à jour avec succès.', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

function updatePriority(priority) {
    axios.patch("/api/tickets/" + ticket.value.data.id + '/priority', { priority: priority })
        .then(response => {
            ticket.value.data.priority = response.data.data.priority;
            ticket.value.data.priority_label_fr = response.data.data.priority_label_fr;
            ticket.value.data.priority_badge_color = response.data.data.priority_badge_color;
            $toast.success('La priorité du ticket a été mise à jour avec succès.', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

const form = {
    assigned_to_id: null,
}

function assignTicketToUser(userId) {
    axios.patch("/api/tickets/" + ticket.value.data.id + '/assigned-to', { assigned_to_id: userId })
        .then(response => {
            ticket.value.data.assigned_to = response.data.data.assigned_to;
            ticket.value.data.assigned_to_id = response.data.data.assigned_to_id;
            $toast.success('Le ticket a été assigné avec succès.', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            });
        })
        .catch(error => {
            httpErrorsHandler(error);
        });
}

function assignTicketToUserWithModal() {
    if (form.assigned_to_id === null){
        $toast.info('Vous devez sélectionner un utilisateur pour l\'assigner au ticket.', {
            position: 'top',
            duration: 2000,
            dismissible: true,
            pauseOnHover: true,
        });
        return;
    }
    if (form.assigned_to_id === ticket.value.data.assigned_to_id){
        $toast.info('L\'utilisateur sélectionné est déjà assigné au ticket.', {
            position: 'top',
            duration: 2000,
            dismissible: true,
            pauseOnHover: true,
        });
        return;
    }
    assignTicketToUser(form.assigned_to_id)
    // Close modal
    const modaleDelete = bootstrap.Modal.getOrCreateInstance(document.getElementById('modalAssignUser'))
    modaleDelete.hide()
}

function resetNotificationNumber() {
    axios.patch("/api/tickets/" + router.currentRoute.value.params.id + "/reset-notification-number")
        .then()
        .catch(error => {
            httpErrorsHandler(error);
        });
}

// Avancement du ticket, du dépôt à la fermeture
const TICKET_STEPS = [
    { value: 'pending', label: 'À traiter' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'resolved', label: 'Résolu' },
    { value: 'closed', label: 'Fermé' },
]
const currentStep = computed(() => TICKET_STEPS.findIndex((step) => step.value === ticket.value?.data.status))
const stepClass = (index) => ({ 'is-done': index < currentStep.value, 'is-current': index === currentStep.value })
</script>

<template>
    <!--Loader-->
    <div class="card" v-show="loading">
        <div class="card-body">
            <loader/>
        </div>
    </div>
    <div v-if="!loading">
        <!-- En-tête : carte avec un liseré de la couleur du statut -->
        <div class="card ticket-header" :style="{ '--ticket-status-color': `var(--vz-${ticket.data.status_badge_color})` }">
            <div class="card-body px-4 py-3">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                        <h4 class="ticket-title mb-2" id="ticket-title">
                            <span class="ticket-title-number">#{{ ticket.data.id }}</span>
                            {{ ticket.data.title }}
                        </h4>
                        <div class="ticket-meta">
                            <span><i class="mdi mdi-label-variant-outline"></i> {{ ticket.data.category.data.name }}</span>
                            <span v-tooltip="ticket.data.created_at_fr"><i class="mdi mdi-clock-outline"></i> Ouvert {{ ticket.data.created_at_fr_human }}</span>
                            <span><i class="mdi mdi-message-text-outline"></i> {{ ticket.data.comments_count }} {{ ticket.data.comments_count > 1 ? 'messages' : 'message' }}</span>
                            <span v-tooltip="ticket.data.updated_at_fr"><i class="mdi mdi-history"></i> Activité {{ ticket.data.updated_at_fr_human }}</span>
                        </div>
                    </div>

                    <!-- Avancement du ticket -->
                    <ol class="ticket-steps">
                        <li v-for="(step, index) in TICKET_STEPS" :key="step.value" :class="stepClass(index)">
                            <span class="ticket-step-dot"><i class="mdi mdi-check" v-if="index < currentStep"></i></span>
                            <span class="ticket-step-label">{{ step.label }}</span>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-xxl-9">
                <!-- Premier message : qui a ouvert le ticket, quand, et ses fichiers -->
                <div class="card ticket-message">
                    <div class="card-body p-4">
                        <div class="d-flex align-items-center gap-3 mb-3">
                            <img :src="'/format-image/'+ticket.data.user.data.profile_picture+'?w=44&h=44&fm=webp&fit=crop'" alt="" class="ticket-message-avatar rounded-circle">
                            <div>
                                <div class="fw-semibold text-body">{{ ticket.data.user.data.first_name }} {{ ticket.data.user.data.last_name }}</div>
                                <div class="ticket-message-meta">a ouvert le ticket · {{ ticket.data.created_at_fr }}</div>
                            </div>
                        </div>
                        <div class="text-body ticket-container" v-html="ticket.data.description"></div>
                        <attachment-files :attachments="ticket.data.attachments" class="mt-3" />
                    </div>
                </div>
                <comments
                    commentable-type="App\Models\Ticket"
                    :commentable-id="ticket.data.id"
                    bs-class="col-12"
                />
            </div>
            <div class="col-xxl-3">
                <div class="card ticket-details">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="card-title mb-0 d-flex align-items-center gap-2">
                            <span class="ticket-details-icon"><i class="mdi mdi-ticket-confirmation-outline"></i></span>
                            Détails du ticket
                        </h5>
                        <router-link :to="{ name: 'tickets.edit', params:{ id: ticket.data.id } }" class="btn btn-sm btn-soft-secondary" v-if="ticket.can.update" title="Modifier le ticket">
                            <i class="ri-edit-2-fill"></i>
                        </router-link>
                    </div>
                    <div class="card-body">
                        <!-- Statut et priorité : ce qu'on regarde en premier -->
                        <div class="row g-2 mb-4">
                            <div class="col-6">
                                <div class="ticket-state" :class="{ 'is-editable': ticket.can.update_details }" id="statusDropdown" :data-bs-toggle="ticket.can.update_details ? 'dropdown' : null" role="button">
                                    <span class="ticket-state-label">Statut</span>
                                    <span :class="'badge fs-12 bg-'+ticket.data.status_badge_color+'-subtle text-'+ticket.data.status_badge_color">{{ ticket.data.status_label_fr }}</span>
                                    <i class="mdi mdi-chevron-down ticket-state-caret" v-if="ticket.can.update_details"></i>
                                </div>
                                <ul class="dropdown-menu" aria-labelledby="statusDropdown">
                                    <li v-if="ticket.data.status !== 'pending'">
                                        <a class="dropdown-item" href="#" @click.prevent="updateStatus('pending')">
                                            <span class="badge bg-danger-subtle text-danger fs-11">A traiter</span>
                                        </a>
                                    </li>
                                    <li v-if="ticket.data.status !== 'in_progress'">
                                        <a class="dropdown-item" href="#" @click.prevent="updateStatus('in_progress')">
                                            <span class="badge bg-secondary-subtle text-secondary fs-11">En cours</span>
                                        </a>
                                    </li>
                                    <li v-if="ticket.data.status !== 'resolved'">
                                        <a class="dropdown-item" href="#" @click.prevent="updateStatus('resolved')">
                                            <span class="badge bg-success-subtle text-success fs-11">Résolu</span>
                                        </a>
                                    </li>
                                    <li v-if="ticket.data.status !== 'closed'">
                                        <a class="dropdown-item" href="#" @click.prevent="updateStatus('closed')">
                                            <span class="badge bg-dark-subtle text-dark fs-11">Fermé</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                            <div class="col-6">
                                <div class="ticket-state" :class="{ 'is-editable': ticket.can.update_details }" id="priorityDropdown" :data-bs-toggle="ticket.can.update_details ? 'dropdown' : null" role="button">
                                    <span class="ticket-state-label">Priorité</span>
                                    <span :class="'badge fs-12 bg-'+ticket.data.priority_badge_color+'-subtle text-'+ticket.data.priority_badge_color">{{ ticket.data.priority_label_fr }}</span>
                                    <i class="mdi mdi-chevron-down ticket-state-caret" v-if="ticket.can.update_details"></i>
                                </div>
                                <ul class="dropdown-menu" aria-labelledby="priorityDropdown">
                                    <li v-if="ticket.data.priority !== 0">
                                        <a class="dropdown-item" href="#" @click.prevent="updatePriority(0)">
                                            <span class="badge bg-info-subtle text-info badge-border fs-11">Normale</span>
                                        </a>
                                    </li>
                                    <li v-if="ticket.data.priority !== 1">
                                        <a class="dropdown-item" href="#" @click.prevent="updatePriority(1)">
                                            <span class="badge bg-warning-subtle text-warning badge-border fs-11">Moyenne</span>
                                        </a>
                                    </li>
                                    <li v-if="ticket.data.priority !== 2">
                                        <a class="dropdown-item" href="#" @click.prevent="updatePriority(2)">
                                            <span class="badge bg-danger-subtle text-danger badge-border fs-11">Élevée</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <!-- Personnes -->
                        <h6 class="ticket-section-title">Personnes</h6>
                        <div class="ticket-person">
                            <img :src="'/format-image/'+ticket.data.user.data.profile_picture+'?w=40&h=40&fm=webp&fit=crop'" alt="" class="ticket-person-avatar rounded-circle">
                            <div class="flex-grow-1 overflow-hidden">
                                <div class="ticket-person-role">Créé par</div>
                                <div class="ticket-person-name text-truncate">{{ ticket.data.user.data.first_name }} {{ ticket.data.user.data.last_name }}</div>
                                <div class="ticket-person-meta text-truncate" v-if="ticket.data.user.data.centers.length > 0">
                                    <i class="mdi mdi-store-outline"></i> {{ ticket.data.user.data.centers[0].data.name }}
                                </div>
                            </div>
                        </div>
                        <div class="ticket-person">
                            <template v-if="ticket.data.assigned_to !== null">
                                <img :src="'/format-image/'+ticket.data.assigned_to.data.profile_picture+'?w=40&h=40&fm=webp&fit=crop'" alt="" class="ticket-person-avatar rounded-circle">
                                <div class="flex-grow-1 overflow-hidden">
                                    <div class="ticket-person-role">Assigné à</div>
                                    <div class="ticket-person-name text-truncate">{{ ticket.data.assigned_to.data.first_name }} {{ ticket.data.assigned_to.data.last_name }}</div>
                                </div>
                            </template>
                            <template v-else>
                                <span class="ticket-person-avatar ticket-person-empty rounded-circle"><i class="mdi mdi-account-question-outline"></i></span>
                                <div class="flex-grow-1">
                                    <div class="ticket-person-role">Assigné à</div>
                                    <div class="ticket-person-name text-muted fw-normal">Non assigné</div>
                                </div>
                            </template>
                            <a href="#" class="ticket-person-action" data-bs-toggle="modal" data-bs-target="#modalAssignUser" v-if="ticket.can.update_details">Changer</a>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <!-- Modal assign user -->
        <div id="modalAssignUser" class="modal fade" tabindex="-1" aria-labelledby="modalAssignUserLabel" aria-hidden="true" style="display: none;">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="modalAssignUserLabel">Affecter un utilisateur</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"> </button>
                    </div>
                    <div class="modal-body">
                        <form-select-search-user
                            :errors="errors"
                            label="Assigné à"
                            name="assigned_to_id"
                            :required="true"
                            :data="form.assigned_to_id"
                            @update:field="form.assigned_to_id = ($event !== null ? $event.id : null)"
                            bs-class="col-md-12"
                            :disabled="false"
                            :taggable="false"
                            :label-hidden="false"
                            :user-pre-selected="null"
                            placeholder="Sélectionnez un utilisateur"
                        />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-soft-dark fw-medium text-decoration-none shadow-none" data-bs-dismiss="modal">Fermer</button>
                        <button type="button" class="btn btn-secondary" @click.prevent="assignTicketToUserWithModal">Valider</button>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
/* Panneau « Détails du ticket » */
.ticket-details-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    border-radius: 0.4rem;
    background-color: var(--vz-secondary-bg-subtle);
    color: var(--vz-secondary);
    font-size: 1.1rem;
}

.ticket-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    position: relative;
    height: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--vz-border-color);
    border-radius: 0.5rem;
    cursor: default;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.ticket-state.is-editable {
    cursor: pointer;
}

.ticket-state.is-editable:hover {
    border-color: var(--vz-secondary);
    background-color: var(--vz-light);
}

.ticket-state-label,
.ticket-section-title {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vz-secondary-color);
}

.ticket-state-caret {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    color: var(--vz-secondary-color);
}

.ticket-section-title {
    margin-bottom: 0.6rem;
}

.ticket-person {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
}

.ticket-person + .ticket-person {
    border-top: 1px dashed var(--vz-border-color);
}

.ticket-person-avatar {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    object-fit: cover;
}

.ticket-person-empty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: var(--vz-light);
    font-size: 1.25rem;
    color: var(--vz-secondary-color);
}

.ticket-person-role {
    font-size: 0.72rem;
    color: var(--vz-secondary-color);
}

.ticket-person-name {
    font-weight: 600;
    color: var(--vz-body-color);
}

.ticket-person-meta {
    font-size: 0.75rem;
    color: var(--vz-secondary-color);
}

.ticket-header {
    border-left: 4px solid var(--ticket-status-color, var(--vz-secondary));
}

.ticket-title {
    font-weight: 600;
    line-height: 1.35;
}

.ticket-title-number {
    margin-right: 0.35rem;
    font-weight: 500;
    color: var(--vz-secondary-color);
}

.ticket-message-avatar {
    width: 2.75rem;
    height: 2.75rem;
    object-fit: cover;
}

.ticket-message-meta {
    font-size: 0.8rem;
    color: var(--vz-secondary-color);
}

.ticket-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 1.25rem;
    font-size: 0.85rem;
    color: var(--vz-secondary-color);
}

.ticket-meta .mdi {
    margin-right: 0.15rem;
}

.ticket-steps {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
}

.ticket-steps li {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    min-width: 5.5rem;
    color: var(--vz-secondary-color);
    font-size: 0.75rem;
}

/* Trait entre deux étapes */
/* Téléphone : la frise passe sous le titre et prend toute la largeur, étapes réparties à parts égales */
@media (max-width: 767.98px) {
    .ticket-steps {
        width: 100%;
    }

    .ticket-steps li {
        flex: 1 1 0;
        min-width: 0;
        text-align: center;
    }
}

.ticket-steps li + li::before {
    content: '';
    position: absolute;
    top: 0.6rem;
    right: calc(50% + 0.75rem);
    left: calc(-50% + 0.75rem);
    height: 2px;
    background-color: var(--vz-border-color);
}

.ticket-step-dot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    border: 2px solid var(--vz-border-color);
    border-radius: 50%;
    background-color: var(--vz-secondary-bg);
    font-size: 0.75rem;
    color: #fff;
}

.ticket-steps li.is-done .ticket-step-dot,
.ticket-steps li.is-current .ticket-step-dot {
    border-color: var(--ticket-status-color);
    background-color: var(--ticket-status-color);
}

.ticket-steps li.is-done + li::before,
.ticket-steps li.is-current::before {
    background-color: var(--ticket-status-color);
}

.ticket-steps li.is-current {
    font-weight: 600;
    color: var(--vz-body-color);
}

.ticket-person-action {
    flex-shrink: 0;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--vz-secondary);
}

.ticket-person-action:hover {
    text-decoration: underline;
}

</style>
